import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/auth-context";
import "./leftBar.scss";
import SidebarToggle from "../navbar/SidebarToggle";

import { getClientLogo, getClientName } from "../../utils/clientBranding";

import healthSunLogoIcon from "../../assets/img/healthsun-icon.svg";
import freedomLogoIcon from "../../assets/img/freedom-icon.svg";
import optimumLogoIcon from "../../assets/img/optimum-icon.svg";
import demoLogoIcon from "../../assets/img/demo-icon.svg";

import communicareIcon from "../../assets/img/communicare-icon.svg";

import securIcon from "../../assets/img/secure-icon.svg";
import ultimateIcon from "../../assets/img/ultimate-icon.svg";
import cshIcon from "../../assets/img/logo/CSH/icon.svg";
import gkIcon from "../../assets/img/logo/GK/icon.svg";
import solisIcon from "../../assets/img/logo/solis/solis-icon.svg"
import libertyIcon from "../../assets/img/logo/Liberty/icon.svg";
import prominenceIcon from "../../assets/img/logo/Prominence/prominance-icon.svg";
import amsoIcon from "../../assets/img/logo/amso/amso-logo-icon.svg";
import aacoIcon from "../../assets/img/logo/aaco/aaco-logo-icon.svg";


const LeftBar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { user, userRole } = useAuth();
  const clientName = getClientName();

  const payerFeatures = JSON.parse(
    process.env.REACT_APP_PAYER_FEATURES || "[]"
  );

  // Always include these menus in the left nav (env may lag until restart).
  const alwaysVisibleMenus = [
    "Custom Settings",
    "Integration Configuration",
    "EDI Mapping Explorer",
    // "Auth Tracking",
  ];
  const navFeatures = alwaysVisibleMenus.reduce(
    (features, menu) =>
      features.includes(menu) ? features : [...features, menu],
    payerFeatures,
  );

  const [activeMenu, setActiveMenu] = useState("");

  const menuItems = {
    "Patient Search": { path: "/PatientSearch", icon: "fh_patient_search_2" },
    "Application": { path: "/ApplicationList", icon: "fh_dashboard_fill" },
    "User Search": { path: "/UserSearch", icon: "fh_user_search_1" },
    "EDI Mapping Explorer": { path: "/EdiInspector", icon: "fh_edi_mapping_explorer" },
    "Custom Settings": { path: "/CustomSettings", icon: "fh_custom_settings" },
    "Integration Configuration": {
      path: "/payer-integration",
      icon: "fh_integration_configuration",
    },
    // "Auth Tracking": {
    //   path: "/AuthTracking",
    //   icon: "fh_select_mem",
    // },
  };

  // Child/sub-routes that should keep their parent menu highlighted
  const menuMatchPaths = {
    "Patient Search": ["/PatientSearch"],
    "Application": ["/ApplicationList", "/ApplicationView"],
    "User Search": ["/UserSearch", "/LinkUser", "/UserDetail"],
    "EDI Mapping Explorer": ["/EdiInspector"],
    "Custom Settings": ["/CustomSettings"],
    "Integration Configuration": ["/payer-integration"],
    // "Auth Tracking": ["/AuthTracking"],
  };

  // Menus listed here are role-restricted. Add roles as needed.
  // Menus not listed are visible to all roles.
  const menuRoleAccess = {
    "User Search": [1, 3],
    "Custom Settings": [3],
    "Integration Configuration": [3],
    "EDI Mapping Explorer": [3],
    "Auth Tracking": [3],
  };

  const canAccessMenu = (feature) => {
    const allowedRoles = menuRoleAccess[feature];
    if (!allowedRoles) {
      return true;
    }
    return allowedRoles.includes(Number(userRole));
  };

  // Sync active menu with URL (including child routes); fall back when unmatched
  useEffect(() => {
    const matched = Object.entries(menuMatchPaths).find(([, paths]) =>
      paths.some(
        (p) =>
          location.pathname === p || location.pathname.startsWith(`${p}/`)
      )
    );

    if (matched) {
      setActiveMenu(matched[0]);
      return;
    }

    const firstVisibleFeature = navFeatures.find((feature) =>
      canAccessMenu(feature),
    );

    if (firstVisibleFeature) {
      setActiveMenu(firstVisibleFeature);
    }
  }, [location.pathname, userRole]);

  const handleNavigation = (event, to) => {
    event.preventDefault();

    if (!user) {
      navigate("/post-logout");
    } else {
      navigate(to);
    }
  };

  const getLogoSource = () => getClientLogo(clientName);

  const getLogoIcon = () => {
    switch (clientName) {
      case "freedom":
        return freedomLogoIcon;
      case "healthsun":
        return healthSunLogoIcon;
      case "optimum":
        return optimumLogoIcon;
      case "demo":
        return demoLogoIcon;
      case "communicare":
        return communicareIcon;
      case "secur":
        return securIcon;
      case "ultimate":
        return ultimateIcon;
      case "csh":
        return cshIcon;
      case "gk":
        return gkIcon;
      case "liberty":
        return libertyIcon
      case "prominence":
        return prominenceIcon
      case "solis":
        return solisIcon
      case "ahaaco":
        return aacoIcon
      case "ahamso":
        return amsoIcon
      default:
        return "";
    }
  };

  const toggleSideBar = () => {
    if (window.innerWidth < 1024) {
      const sidebarToggle = document.body.querySelector("#sidebarToggle");

      if (sidebarToggle) {
        document.body.classList.toggle("sb-sidenav-toggled");
      }
    }
  };

  // ✅ COMMON LOGO CLICK HANDLER
  const handleLogoClick = (e) => {
    const defaultFeature =
      payerFeatures.length > 0 ? payerFeatures[0] : null;

    const defaultPath =
      defaultFeature && menuItems[defaultFeature]
        ? menuItems[defaultFeature].path
        : "/";

    if (defaultFeature) {
      setActiveMenu(defaultFeature);   // ✅ FIXED
    }

    handleNavigation(e, defaultPath);
  };

  return (
    <nav className="sb-sidenav accordion" id="sidenavAccordion">
      <div className="header-section d-flex justify-content-between align-items-center">
        <div className="client-logo">
          <img
            src={getLogoSource()}
            alt="Client Logo"
            onClick={handleLogoClick}
          />
        </div>

        <SidebarToggle />

        <img
          src={getLogoIcon()}
          alt="Client Logo"
          className="client-icon w40"
          onClick={handleLogoClick}
        />
      </div>

      <div className="sb-sidenav-menu">
        <div className="nav">
          {navFeatures.map((feature) => {
            if (!canAccessMenu(feature)) {
              return null;
            }

            if (!menuItems[feature]) {
              return null;
            }

            return (
              <a
                key={feature}
                className={
                  "nav-link " + (activeMenu === feature ? "active" : "")
                }
                href={menuItems[feature].path}
                onClick={(e) => {
                  setActiveMenu(feature);
                  handleNavigation(e, menuItems[feature].path);
                  toggleSideBar();
                }}
                  data-bs-toggle="tooltip"
                  data-bs-placement="right"
                  title={feature}
                >
                  <div className="sb-nav-link-icon">
                    <i className={menuItems[feature].icon}></i>
                  </div>

                  <span>{feature}</span>
                </a>
            );
          })}
        </div>
      </div>

      <div className="sb-sidenav-footer">
        <div className="footer-info">
          <div className="small-txt">Powered By</div>
          <img
            src={require("../../assets/img/aaneel-poweredby.svg").default}
            alt="mySvgImage"
          />
        </div>
      </div>
    </nav>
  );
};

export default LeftBar;
